<template>
  <div class="csv-uploader">
    <v-sheet class="upload-zone" :class="{ 'drag-over': isDragOver, 'has-file': hasFile }" @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop">
      <input ref="fileInput" type="file" accept=".csv,.txt" class="file-input" @change="onFileSelected" />

      <div v-if="!hasFile" class="upload-placeholder">
        <v-icon size="64" color="primary" class="mb-4"> mdi-cloud-upload-outline </v-icon>
        <h3 class="upload-title">Arrastra tu archivo CSV aquí</h3>
        <p class="upload-subtitle">o haz clic para seleccionar un archivo desde tu computadora</p>

        <v-btn color="primary" variant="outlined" class="mt-4" @click="openFilePicker">
          <v-icon left>mdi-file-delimited</v-icon>
          Seleccionar archivo CSV
        </v-btn>

        <div class="upload-hints mt-4">
          <span class="hint-item">
            <v-icon size="14" class="mr-1">mdi-check-circle-outline</v-icon>
            CSV (; o ,)
          </span>
          <span class="hint-divider">|</span>
          <span class="hint-item">
            <v-icon size="14" class="mr-1">mdi-check-circle-outline</v-icon>
            Máx 10MB
          </span>
          <span class="hint-divider">|</span>
          <span class="hint-item">
            <v-icon size="14" class="mr-1">mdi-check-circle-outline</v-icon>
            Máx 5000 registros
          </span>
          <span class="hint-divider">|</span>
          <span class="hint-item">
            <v-icon size="14" class="mr-1">mdi-check-circle-outline</v-icon>
            UTF-8
          </span>
        </div>

        <v-alert v-if="error" type="error" variant="tonal" class="mt-4" density="compact">
          {{ error }}
        </v-alert>
      </div>

      <div v-else class="upload-file-info">
        <v-icon size="48" color="success" class="mb-2"> mdi-file-delimited-outline </v-icon>
        <div class="file-name">{{ file.name }}</div>
        <div class="file-size">{{ formatFileSize(file.size) }}</div>

        <v-btn color="error" variant="text" size="small" class="mt-2" @click="$emit('clear')">
          <v-icon left>mdi-close</v-icon>
          Quitar archivo
        </v-btn>
      </div>
    </v-sheet>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const emit = defineEmits(['file-selected', 'clear'])

const props = defineProps({
  file: { type: File, default: null },
  error: { type: String, default: '' },
})

const isDragOver = ref(false)
const fileInput = ref(null)

const hasFile = computed(() => !!props.file)

const openFilePicker = () => {
  fileInput.value?.click()
}

const onDragOver = () => {
  isDragOver.value = true
}

const onDragLeave = () => {
  isDragOver.value = false
}

const onDrop = (event) => {
  isDragOver.value = false
  const files = event.dataTransfer?.files
  if (files?.length > 0) {
    validateAndSelect(files[0])
  }
}

const onFileSelected = (event) => {
  const files = event.target?.files
  if (files?.length > 0) {
    validateAndSelect(files[0])
  }
}

const validateAndSelect = (file) => {
  const validExtensions = ['.csv', '.txt']
  const ext = '.' + file.name.split('.').pop().toLowerCase()

  if (!validExtensions.includes(ext)) {
    emit('error', 'Solo se permiten archivos con extensión .csv')
    return
  }

  if (file.size > 10 * 1024 * 1024) {
    emit('error', 'El archivo excede el tamaño máximo de 10MB')
    return
  }

  emit('file-selected', file)
}

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}
</script>

<style scoped>
.upload-zone {
  border: 2px dashed #cbd5e1;
  border-radius: 16px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fafafa;
  position: relative;
}

.upload-zone:hover {
  border-color: #0a192f;
  background: #f0f5ff;
}

.upload-zone.drag-over {
  border-color: #0a192f;
  background: #e8f0fe;
  transform: scale(1.01);
}

.upload-zone.has-file {
  border-style: solid;
  border-color: #22c55e;
  background: #f0fdf4;
}

.file-input {
  display: none;
}

.upload-title {
  color: #1e293b;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.upload-subtitle {
  color: #64748b;
  font-size: 0.9rem;
}

.upload-file-info {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.file-name {
  font-weight: 600;
  color: #0a192f;
  font-size: 1.1rem;
  word-break: break-all;
  max-width: 100%;
}

.file-size {
  color: #64748b;
  font-size: 0.85rem;
  margin-top: 4px;
}

.upload-hints {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
  color: #94a3b8;
  font-size: 0.8rem;
}

.hint-item {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.hint-divider {
  color: #e2e8f0;
}
</style>
